import classNames from "classnames";
import Link from "next/link";
import { MouseEvent, ReactNode } from "react";
import { DeepSearchIcon, MeterIcon } from "design_system";

type QueryButtonTagProps = {
  q?: string;
  isYesNoQuery?: boolean;
  href?: string;
  onClick?: (event: MouseEvent) => void;
  icon?: "search" | "follow-up" | "deep-search";
  variant?: "default" | "gradient";
  children?: ReactNode;
  loading?: boolean;
  prefixText?: string;
  allowTextWrap?: boolean;
};

// Leading icon components map
const leadingIcons = {
  "deep-search": <DeepSearchIcon />,
  "yes-no": <MeterIcon data-testid="yes-no-icon" />,
};

// Trailing icon components map
const trailingIcons = {
  search: <i className="icon-arrow-right text-fg-muted h-4 w-4" />,
  default: <i className="icon-arrow-up text-fg-muted bold-icon h-4 w-4" />,
};

const QueryButtonTag = ({
  q,
  isYesNoQuery,
  children,
  loading = false,
  href,
  onClick,
  icon = "search",
  prefixText,
  variant = "default",
  allowTextWrap = false,
  ...rest
}: QueryButtonTagProps) => {
  const handleClick = (event: MouseEvent) => {
    if (loading) {
      event.preventDefault();
      return;
    }
    if (onClick) onClick(event);
  };

  const sharedClassNames = classNames(
    "flex items-center justify-between w-full gap-4 px-3 text-left rounded-xl transition-colors",
    allowTextWrap ? "py-2 min-h-10" : "h-10",
    variant === "gradient"
      ? "sparkle-box-full-border-gradient"
      : "border-border-base border bg-bg-faint hover:bg-bg-subtle"
  );

  // Get the appropriate leading icon
  const getLeadingIcon = () => {
    if (icon === "deep-search")
      return <span className="flex-none">{leadingIcons["deep-search"]}</span>;
    if (isYesNoQuery)
      return <span className="flex-none">{leadingIcons["yes-no"]}</span>;
    return null;
  };

  // Get the appropriate trailing icon
  const getTrailingIcon = () => {
    return (
      <span className="flex-none">
        {trailingIcons[icon === "search" ? "search" : "default"]}
      </span>
    );
  };

  const content = (
    <>
      <div className="flex items-center w-full gap-3">
        {getLeadingIcon()}
        {loading ? (
          <div
            className={`bg-bg-subtle animate-pulse h-2.5 rounded-xl w-[90%]`}
          />
        ) : children ? (
          children
        ) : (
          <div className="flex items-center gap-[6px]">
            {prefixText && (
              <>
                <p className="tiny-mono-semibold uppercase text-fg-muted text-nowrap pt-[2px]">
                  {prefixText}
                </p>
                <span className="text-fg-muted">·</span>
              </>
            )}
            <p
              className={classNames("sm-normal mt-0 mb-0", {
                "line-clamp-1": !allowTextWrap,
              })}
            >
              {q}
            </p>
          </div>
        )}
      </div>
      {getTrailingIcon()}
    </>
  );

  if (!href) {
    return (
      <button onClick={handleClick} className={sharedClassNames} {...rest}>
        {content}
      </button>
    );
  }

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={sharedClassNames}
      {...rest}
    >
      {content}
    </Link>
  );
};

export { QueryButtonTag };
